import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Briefcase, MapPin, Clock, Building2, Users, Plus, Edit2, 
  Trash2, ChevronDown, ChevronUp, Eye, AlertCircle, Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminJobsPage = () => {
  const { user } = useAuth();
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPosting, setEditingPosting] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchPostings();
  }, []);

  const fetchPostings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/job-postings/drafts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch job postings');
      
      const data = await response.json();
      setPostings(data.postings || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDrafts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/job-postings/seed-drafts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to seed drafts');
      
      await fetchPostings();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this draft posting?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/job-postings/drafts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete posting');
      
      await fetchPostings();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getEmploymentBadge = (type) => {
    const styles = {
      full_time: 'bg-green-100 text-green-800',
      part_time: 'bg-yellow-100 text-yellow-800',
      contract: 'bg-purple-100 text-purple-800'
    };
    const labels = {
      full_time: 'Full Time',
      part_time: 'Part Time',
      contract: 'Contract'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {labels[type] || type}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-jobs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="w-7 h-7 text-blue-600" />
              Staff Job Postings
            </h1>
            <p className="text-gray-600 mt-1">
              Internal draft management for future staff roles
            </p>
          </div>
          
          {isSuperAdmin && (
            <div className="flex gap-2">
              {postings.length === 0 && (
                <button
                  onClick={handleSeedDrafts}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                  data-testid="seed-drafts-btn"
                >
                  Seed Sample Drafts
                </button>
              )}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                data-testid="create-posting-btn"
              >
                <Plus className="w-4 h-4" />
                Create Draft
              </button>
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-amber-800 font-medium">Draft Mode Only</p>
            <p className="text-sm text-amber-700 mt-1">
              These job postings are drafts for internal planning. The public /careers page is disabled and hidden. 
              Publishing will be enabled in a future release.
            </p>
          </div>
        </div>

        {/* Access Level Banner */}
        {!isSuperAdmin && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800 font-medium">View-Only Access</p>
              <p className="text-sm text-blue-700 mt-1">
                You have read-only access to job postings. Contact a Super Admin to create or modify drafts.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Empty State */}
        {postings.length === 0 && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Draft Postings</h3>
            <p className="text-gray-600 mb-6">
              {isSuperAdmin 
                ? 'Get started by seeding sample drafts or creating a new posting.'
                : 'No draft job postings have been created yet.'}
            </p>
          </div>
        )}

        {/* Job Postings List */}
        <div className="space-y-4">
          {postings.map((posting) => (
            <div 
              key={posting.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              data-testid={`job-posting-${posting.id}`}
            >
              {/* Header Row */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(posting.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900">{posting.title}</h3>
                      {getEmploymentBadge(posting.employment_type)}
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        Draft
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {posting.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {posting.location}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {isSuperAdmin && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingPosting(posting); }}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                          data-testid={`edit-posting-${posting.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(posting.id); }}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                          data-testid={`delete-posting-${posting.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {expandedId === posting.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === posting.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <div className="space-y-4">
                    {/* Summary */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Summary</h4>
                      <p className="text-sm text-gray-600">{posting.summary}</p>
                    </div>

                    {/* Salary */}
                    {posting.salary_range && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Compensation</h4>
                        <p className="text-sm text-gray-600">{posting.salary_range}</p>
                      </div>
                    )}

                    {/* Responsibilities */}
                    {posting.responsibilities?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Responsibilities</h4>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          {posting.responsibilities.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Qualifications */}
                    {posting.qualifications?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Required Qualifications</h4>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          {posting.qualifications.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Preferred */}
                    {posting.preferred_qualifications?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Preferred Qualifications</h4>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          {posting.preferred_qualifications.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Benefits */}
                    {posting.benefits?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Benefits</h4>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          {posting.benefits.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="pt-3 border-t border-gray-200 flex items-center gap-4 text-xs text-gray-500">
                      <span>Created: {new Date(posting.created_at).toLocaleDateString()}</span>
                      {posting.updated_at && (
                        <span>Updated: {new Date(posting.updated_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Stats */}
        {postings.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                {postings.length} draft posting{postings.length !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Publishing disabled
              </span>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || editingPosting) && (
          <JobPostingModal
            posting={editingPosting}
            onClose={() => { setShowCreateModal(false); setEditingPosting(null); }}
            onSave={fetchPostings}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

// Modal Component for Create/Edit
const JobPostingModal = ({ posting, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: posting?.title || '',
    department: posting?.department || '',
    location: posting?.location || 'Grandville, Michigan',
    employment_type: posting?.employment_type || 'full_time',
    summary: posting?.summary || '',
    responsibilities: posting?.responsibilities?.join('\n') || '',
    qualifications: posting?.qualifications?.join('\n') || '',
    preferred_qualifications: posting?.preferred_qualifications?.join('\n') || '',
    benefits: posting?.benefits?.join('\n') || '',
    salary_range: posting?.salary_range || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        responsibilities: formData.responsibilities.split('\n').filter(s => s.trim()),
        qualifications: formData.qualifications.split('\n').filter(s => s.trim()),
        preferred_qualifications: formData.preferred_qualifications.split('\n').filter(s => s.trim()),
        benefits: formData.benefits.split('\n').filter(s => s.trim())
      };

      const url = posting 
        ? `${API_URL}/api/job-postings/drafts/${posting.id}`
        : `${API_URL}/api/job-postings/drafts`;
      
      const response = await fetch(url, {
        method: posting ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save posting');
      }

      await onSave();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {posting ? 'Edit Draft Posting' : 'Create Draft Posting'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                data-testid="job-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                data-testid="job-department-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                data-testid="job-location-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
              <select
                value={formData.employment_type}
                onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="job-employment-type-select"
              >
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Summary *</label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              data-testid="job-summary-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Responsibilities * <span className="font-normal text-gray-500">(one per line)</span>
            </label>
            <textarea
              value={formData.responsibilities}
              onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              data-testid="job-responsibilities-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Required Qualifications * <span className="font-normal text-gray-500">(one per line)</span>
            </label>
            <textarea
              value={formData.qualifications}
              onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              data-testid="job-qualifications-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preferred Qualifications <span className="font-normal text-gray-500">(one per line)</span>
            </label>
            <textarea
              value={formData.preferred_qualifications}
              onChange={(e) => setFormData({ ...formData, preferred_qualifications: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="job-preferred-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Benefits <span className="font-normal text-gray-500">(one per line)</span>
            </label>
            <textarea
              value={formData.benefits}
              onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="job-benefits-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
            <input
              type="text"
              value={formData.salary_range}
              onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
              placeholder="e.g., Competitive, based on experience"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="job-salary-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              data-testid="save-posting-btn"
            >
              {saving ? 'Saving...' : (posting ? 'Update Draft' : 'Create Draft')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminJobsPage;
